import {
  Appointment,
  Cost,
  DISCOUNT_TYPE,
  DISCOUNT_TYPE_CALC,
  DiscountType,
  LocalServiceEntity,
  PAYMENT_STATUS,
  PAY_MODES,
  PatientProfile,
  RECEIPT_DISCOUNT_TYPES,
  RECEIPT_FLOW_STATES,
  RECEIPT_PAYMENT_MODE_ID,
  RECEIPT_TYPE,
  TCreateReceiptBody,
  TCreateReceiptResponse,
  TReceipt,
  TReceiptEntity,
  TReceiptSku,
  TSelectedSkuItem,
  TStorePaymode,
} from "@elixir/types";
import dayjs from "dayjs";
import { ReceiptStore } from "../../stores/src/receipt-store/types";
import { massageMobileNumberForReceipt } from "../../../apps/dr/src/utils/receipt-utils";

export function getDefaultReceiptPaymode({
  data,
  total = 0,
  index = 0,
}: {
  data?: TStorePaymode[];
  total?: number;
  index?: number;
}): TStorePaymode {
  if (!data?.length) {
    return {
      amount: total,
      paymode: PAY_MODES[index].id,
      amountToShowWithType: total.toString(),
    };
  }
  const amountTotal =
    ((total - (data?.[data.length - 1]?.amount || 0)) * 100) / 100;
  const finalAmount = amountTotal > 0 ? amountTotal : 0;
  return {
    amount: finalAmount,
    paymode: PAY_MODES[index].id,
    amountToShowWithType: finalAmount.toString(),
  };
}

export function getDefaultReceiptService({
  receiptSku,
  skuItem,
}: {
  receiptSku?: TReceiptSku;
  skuItem?: TSelectedSkuItem;
}): LocalServiceEntity {
  if (receiptSku) {
    const {
      quantity,
      discount_amount,
      discount_value,
      discount_type,
      sku_item,
    } = receiptSku;

    const discountTypeObject = RECEIPT_DISCOUNT_TYPES.find(
      (dty) => dty.id === discount_type
    );

    return {
      id: sku_item.service_id || 1,
      name: sku_item?.service_name || "",
      cost: {
        curr: "INR",
        price: sku_item?.price || 0,
        discountToShowWithType: discount_amount.toString(),
        qty: quantity,
        discount: discount_value,
        discountType: discountTypeObject
          ? discountTypeObject
          : RECEIPT_DISCOUNT_TYPES[0],
      },
    };
  }

  const { price, service_id, service_name } = skuItem || {};

  return {
    id: service_id || 0,
    name: service_name || "",
    cost: {
      curr: "INR",
      price: price || 0,
      discountToShowWithType: "0",
      qty: 1,
      discount: 0,
      discountType: RECEIPT_DISCOUNT_TYPES[0],
    },
  };
}

export function calculateReceiptBreakdown({
  services,
  paymodes,
  additionalDiscountConfig,
}: {
  services: LocalServiceEntity[];
  paymodes: TStorePaymode[];
  additionalDiscountConfig: Pick<
    Cost & {
      discountType: DiscountType;
      discountToShowWithType: string;
    },
    "discountType" | "discount"
  >;
}) {
  let subTotal = 0;
  let totalDiscountAcrossServices = 0;

  services.forEach((s) => {
    if (s.cost.price) {
      const lineTotal = s.cost.price * (s.cost.qty || 0);
      subTotal += lineTotal;

      if (s.cost.discount) {
        const discountedValue = DISCOUNT_TYPE_CALC[s.cost.discountType.id](
          s.cost.price,
          s.cost.discount,
          s.cost.qty || 0
        );

        totalDiscountAcrossServices += lineTotal - discountedValue;
      }
    }
  });

  subTotal = roundToTwoDecimals(subTotal);
  totalDiscountAcrossServices = roundToTwoDecimals(totalDiscountAcrossServices);

  let grandTotal = subTotal - totalDiscountAcrossServices;

  const additionalDiscountAmount =
    grandTotal -
    DISCOUNT_TYPE_CALC[additionalDiscountConfig.discountType.id](
      grandTotal,
      additionalDiscountConfig.discount || 0,
      1
    );

  grandTotal = roundToTwoDecimals(
    subTotal - totalDiscountAcrossServices - additionalDiscountAmount
  );

  const paymodeSum = roundToTwoDecimals(
    paymodes.reduce((acc, curr) => {
      return acc + (curr.amount ?? 0);
    }, 0)
  );

  const amountDue = roundToTwoDecimals(grandTotal - paymodeSum);

  const returnTotalDiscountAcrossServices = isNaN(totalDiscountAcrossServices)
    ? 0
    : totalDiscountAcrossServices;

  const topUpDiscount = isNaN(additionalDiscountAmount)
    ? 0
    : roundToTwoDecimals(additionalDiscountAmount);

  const totalDiscountOnEntireBill = roundToTwoDecimals(
    returnTotalDiscountAcrossServices + topUpDiscount
  );

  return {
    subTotal: isNaN(subTotal) ? 0 : subTotal,
    additionalDiscount: topUpDiscount,
    totalDiscount: returnTotalDiscountAcrossServices,
    grandTotal: isNaN(grandTotal) ? 0 : grandTotal,
    amountDue,
    amountPaid: paymodeSum,
    totalDiscountOnEntireBill,
  };
}

export function roundToTwoDecimals(num: number): number {
  return Math.round(num * 100) / 100;
}

export function calculateReceiptPaymode({
  grandTotal,
  paymodes,
}: {
  grandTotal: number;
  paymodes: TStorePaymode[];
}): TStorePaymode[] {
  if (paymodes.length === 1) {
    return [
      {
        ...paymodes[0],
        amount: grandTotal,
        amountToShowWithType: grandTotal.toString(),
      },
    ];
  }
  if (paymodes.length === 2) {
    if (!grandTotal) {
      return [
        { ...paymodes[0], amount: 0, amountToShowWithType: "0" },
        { ...paymodes[1], amount: 0, amountToShowWithType: "0" },
      ];
    }
    const amountCalculated = grandTotal - paymodes[0].amount;
    return [
      { ...paymodes[0] },
      {
        ...paymodes[1],
        amount: amountCalculated,
        amountToShowWithType: amountCalculated.toString(),
      },
    ];
  }

  return [];
}

/** Validation funtion would return if the data provided to them are valid or not. And if they return silent as true, it means block create / update button on UI for receipt, but don't show error explicitly */
export function validateCreateReceiptStore(
  data: Pick<ReceiptStore, "services" | "additionalDiscountConfig" | "paymodes">
): {
  valid: boolean;
  error?: string;
} {
  const { services, additionalDiscountConfig, paymodes } = data;

  if (!services.length) {
    return {
      valid: false,
    };
  }

  for (let i = 0; i < services.length; i++) {
    const serviceValidation = validateOneServiceEntity(services[i]);
    if (!serviceValidation.valid) {
      return serviceValidation;
    }
  }

  // for (let i = 0; i < paymodes.length; i++) {
  //   const { amount } = paymodes[i];
  //   if (typeof amount !== "number" || amount < 0) {
  //     return { valid: false, error: "Invalid Paymode amount" };
  //   }
  // }

  const { grandTotal, amountPaid, subTotal } = calculateReceiptBreakdown({
    services,
    additionalDiscountConfig,
    paymodes,
  });

  if (amountPaid < 0 && subTotal > 0) {
    return { valid: false, error: "Invalid Additional Discount" };
  }

  if (grandTotal < amountPaid) {
    return {
      valid: false,
      error: "Sum of paymode amount cannot be greater than grand total",
    };
  }

  return { valid: true };
}

type ServiceErrorKey = "S_SERVICE" | "S_AMOUNT" | "S_QUANTITY" | "S_DISCOUNT";

/** Validation funtion would return if the data provided to them are valid or not. And if they return silent as true, it means block create / update button on UI for receipt, but don't show error explicitly */
export function validateOneServiceEntity({
  id,
  name,
  cost,
}: LocalServiceEntity): {
  valid: boolean;
  error?: string;
  errorKey?: ServiceErrorKey;
} {
  const { discount, discountType, discountToShowWithType, price, qty } = cost;

  if (!name) {
    return {
      valid: false,
      // error: "Missing Service Name",
      errorKey: "S_SERVICE",
    };
  }

  if (typeof price !== "number") {
    return { valid: false, error: "Invalid Price", errorKey: "S_AMOUNT" };
  }

  if (typeof qty !== "number" || qty < 1 || !qty) {
    return { valid: false, error: "Invalid Quantity", errorKey: "S_QUANTITY" };
  }

  if (discount) {
    if (
      (discountType.id === DISCOUNT_TYPE.AMOUNT &&
        discount > price * (qty || 0)) ||
      (discountType.id === DISCOUNT_TYPE.PERCENTAGE && discount > 100)
    ) {
      return {
        valid: false,
        error: "Discount cannot be more than service cost",
        errorKey: "S_DISCOUNT",
      };
    }
  }

  return { valid: true };
}

export function getPositiveNumber(value: string) {
  return value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
}

export function getCreateReceiptPayload(
  data: ReceiptStore,
  isPaymentLink: boolean = false
): TCreateReceiptBody {
  const { additionalDiscount, amountDue, amountPaid } =
    calculateReceiptBreakdown({
      services: data.services,
      additionalDiscountConfig: data.additionalDiscountConfig,
      paymodes: data.paymodes,
    });

  const receiptType: RECEIPT_TYPE =
    data.receiptState === "settle-due"
      ? RECEIPT_TYPE.DUE_PAYMENT
      : amountPaid >= 0
        ? RECEIPT_TYPE.INCLINIC
        : RECEIPT_TYPE.REFUND;

  const receiptAmount = Number(
    data.services
      .reduce((acc, curr) => {
        const na = DISCOUNT_TYPE_CALC[curr.cost.discountType.id](
          curr.cost.price,
          curr.cost.discount || 0,
          curr.cost.qty || 0
        );
        return acc + na;
      }, 0)
      .toFixed(2)
  );

  const receiptPayments = data.paymodes.map((pm) => ({
    amount: Number(pm.amount.toFixed(2)),
    paymode: pm.paymode,
  }));

  const receiptSkus = data.services.map((service) => {
    const net_amount = DISCOUNT_TYPE_CALC[service.cost.discountType.id](
      service.cost.price,
      service.cost.discount || 0,
      service.cost.qty || 0
    );
    return {
      service_id: service.id || null,
      service_name: service.name,
      quantity: service.cost.qty || 0,
      price: Number(service.cost.price.toFixed(2)),
      amount: Number((service.cost.price * (service.cost.qty || 0)).toFixed(2)),
      discount_value: Number((service.cost.discount || 0).toFixed(2)),
      discount_amount: Number(
        (service.cost.price * (service.cost.qty || 0) - net_amount).toFixed(2)
      ),
      discount_type: service.cost.discountType.id,
      net_amount: Number(net_amount.toFixed(2)),
    };
  });

  return {
    payload: {
      amount_due: receiptType === RECEIPT_TYPE.DUE_PAYMENT ? 0 : amountDue || 0,
      remarks: data.remarks,
      additional_discount_amount: Number((additionalDiscount || 0).toFixed(2)),
      additional_discount_type: data.additionalDiscountConfig.discountType.id,
      additional_discount_value: Number(
        (data.additionalDiscountConfig.discount || 0).toFixed(2)
      ),
      name: data.patientName,
      mobile: massageMobileNumberForReceipt(
        data?.fullPhoneNumber || data?.phoneNumber
      ),
      net_amount: Number(amountPaid.toFixed(2)),
      receipt_amount: receiptAmount,
      receipt_payments: receiptPayments,
      receipt_skus: receiptSkus,
      ref_transaction_id: data.paymentId,
    },
    meta: {
      clinic_id: data.clinicId!,
      patient_oid: data.patientId!,
      aid: data?.aid,
      ...(isPaymentLink && { request_payment: true }),
      receipt_type: receiptType,
    },
  };
}

export function getSettleDueReceiptPayload(
  data: ReceiptStore
): TCreateReceiptBody {
  const receiptPayments = data.paymodes.map((pm) => ({
    amount: Number(pm.amount.toFixed(2)),
    paymode: pm.paymode,
  }));

  return {
    payload: {
      amount_due: 0,
      remarks: data?.receipt?.remarks || "",
      additional_discount_amount: 0,
      additional_discount_type: data.additionalDiscountConfig.discountType.id,
      additional_discount_value: Number(
        (data.additionalDiscountConfig.discount || 0).toFixed(2)
      ),
      name: data.patientName,
      mobile: massageMobileNumberForReceipt(
        data?.fullPhoneNumber || data?.phoneNumber
      ),
      net_amount: data.amountSettled || 0,
      receipt_amount: data.amountSettled || 0,
      receipt_payments: receiptPayments,
      receipt_skus: [],
      ref_transaction_id: data.paymentId,
    },
    meta: {
      clinic_id: data.clinicId!,
      patient_oid: data.patientId!,
      aid: data?.aid,
      receipt_type: RECEIPT_TYPE.DUE_PAYMENT,
    },
  };
}

export function getReceiptStoreDataFromAppointmentReceipt({
  appointment,
  receiptId,
}: {
  appointment: Appointment;
  receiptId?: number;
}) {
  const { patient, clinic_id, formData, receipts, aid, service } = appointment;
  const { fln, fn, mobile, oid, amount_pending } = patient;
  const uhidFromApt = formData?.find((item) => item.key === "uhid")?.value;

  const receipt =
    receipts.find((item) => item.receipt_id === receiptId) || null;

  const additionalDiscountConfig = !!receipt
    ? {
        discountToShowWithType: `${receipt?.additional_discount_value}${
          receipt?.additional_discount_type === "PERCENTAGE" ? "%" : ""
        }`,
        discountType:
          RECEIPT_DISCOUNT_TYPES.find(
            (dType) => dType.id === receipt?.additional_discount_type
          ) || RECEIPT_DISCOUNT_TYPES[0],
        discount: receipt?.additional_discount_value,
      }
    : {
        discount: 0,
        discountToShowWithType: "0",
        discountType: RECEIPT_DISCOUNT_TYPES[0],
      };

  const receiptServices = receipt
    ? receipt.receipt_sku?.map((item) =>
        getDefaultReceiptService({ receiptSku: item })
      )
    : service && service.conf_id !== "fallback-conf" && service.price
      ? [
          getDefaultReceiptService({
            skuItem: {
              service_id: service.service_id,
              service_name: service.service_name,
              price: service.price,
            },
          }),
        ]
      : [getDefaultReceiptService({})];

  const receiptPaymodes = receipt
    ? receipt.receipt_payment
    : [getDefaultReceiptPaymode({})];

  // Get country code: priority is phone.code > ccd > default +91
  const countryCodeFromPhone = (patient as any)?.phone?.code;
  const countryCodeFromCcd = (patient as any)?.ccd;
  const countryCode = countryCodeFromPhone || countryCodeFromCcd || "+91";

  let fullPhoneNumber = mobile || "";
  if (fullPhoneNumber && !fullPhoneNumber.startsWith("+")) {
    fullPhoneNumber = `${countryCode}${fullPhoneNumber}`;
  }

  const receiptStoreData: Partial<ReceiptStore> = {
    receiptState: receipt
      ? RECEIPT_FLOW_STATES.EDIT_RECEIPT
      : RECEIPT_FLOW_STATES.CREATE_RECEIPT,
    patientName: fln || fn,
    phoneNumber: mobile?.substring(mobile.length - 10) || "",
    fullPhoneNumber: fullPhoneNumber,
    uhid: uhidFromApt || "",
    patientId: oid || "",
    clinicId: clinic_id,
    receipt: receipt,
    services: receiptServices,
    paymodes: receiptPaymodes,
    patientOwes: !!amount_pending ? amount_pending : 0,
    amountSettled: !!amount_pending ? amount_pending : 0,
    additionalDiscountConfig,
    aid,
    selectedAppointment: appointment,
    receiptId,
    remarks: receipt?.remarks || "",
    paymentId: receipt?.ref_trx_id || "",
  };

  return receiptStoreData;
}

export function createReceiptResponseToAppointmentReceiptFormat(
  data: TCreateReceiptResponse
): TReceipt {
  return {
    ...data,
    additional_discount_amount: data.additional_discount_amount,
    additional_discount_type: data.additional_discount_type,
    additional_discount_value: data.additional_discount_value,
    mobile: data.mobile,
    name: data.name,
    net_amount: data.net_amount,
    receipt_amount: data.receipt_amount,
    receipt_id: data.receipt_id!,
    receipt_number: data?.receipt_number,
    business_receipt_number: data?.business_receipt_number,
    receipt_payment: data?.receipt_payment || [],
    receipt_url: data?.receipt_url!,
    remarks: data?.remarks,
    amount_due: data.amount_due! || 0,
    receipt_sku: data.receipt_sku,
  };
}

export function getReceiptObjectForAddAppointment({
  clinicId,
  aid,
  selectedSkus,
  selectedPatient,
  selectedPaymode,
}: {
  clinicId: string;
  aid: string;
  selectedSkus: Array<TSelectedSkuItem>;
  selectedPatient: PatientProfile;
  selectedPaymode?: RECEIPT_PAYMENT_MODE_ID;
}): TCreateReceiptBody {
  const totalAmount = selectedSkus.reduce(
    (currAmount, sku) => currAmount + sku.price,
    0
  );

  // Construct full phone number with country code: priority is phone.code > ccd > default +91
  let fullPhoneNumber = selectedPatient.mobile || "";
  const countryCode =
    selectedPatient?.phone?.code || (selectedPatient as any)?.ccd || "+91";
  if (fullPhoneNumber && !fullPhoneNumber.startsWith("+")) {
    fullPhoneNumber = `${countryCode}${fullPhoneNumber}`;
  }

  return {
    meta: {
      clinic_id: clinicId,
      patient_oid: selectedPatient.oid,
      aid,
    },
    payload: {
      remarks: "",
      amount_due: 0,
      name: `${selectedPatient.fn}`,
      mobile: fullPhoneNumber,
      receipt_amount: totalAmount || 0,
      additional_discount_value: 0,
      additional_discount_type: DISCOUNT_TYPE.AMOUNT,
      additional_discount_amount: 0,
      net_amount: totalAmount || 0,
      receipt_payments: [
        {
          amount: totalAmount || 0,
          paymode: selectedPaymode || RECEIPT_PAYMENT_MODE_ID.CASH,
        },
      ],
      receipt_skus: selectedSkus.map((sku) => ({
        service_id: sku?.service_id || null,
        service_name: sku.service_name,
        quantity: 1,
        price: sku?.price || 0,
        amount: sku?.price || 0,
        discount_type: DISCOUNT_TYPE.AMOUNT,
        discount_value: 0,
        discount_amount: 0,
        net_amount: sku?.price || 0,
      })),
    },
  };
}

export function getPaymentStatusMapping(status: PAYMENT_STATUS) {
  switch (status) {
    case PAYMENT_STATUS.UNBILLED:
      return "Unbilled";
    case PAYMENT_STATUS.SUCCESS:
      return "Received";
    case PAYMENT_STATUS.TO_REFUND:
      return "Refund Pending";
    case PAYMENT_STATUS.REFUNDED:
    case PAYMENT_STATUS.REFUND_PROCESSED:
      return "Refunded";
    case PAYMENT_STATUS.CREATED:
      return "Pending";
    case PAYMENT_STATUS.CANCELLED:
      return "Cancelled";
    case PAYMENT_STATUS.FAILED:
      return "Failed";
    default:
      return "Received";
  }
}

export function multipleReceiptsToSeperateRowForQueue(
  apts: Appointment[]
): TReceiptEntity[] {
  return apts
    .reduce(
      (currValue, apt) => {
        const uhid =
          apt.formData.find((item) => item.key === "uhid")?.value || "";
        const newReceiptEntities: Array<Omit<TReceiptEntity, "idx">> = [];
        if (apt.receipts.length >= 1) {
          apt.receipts.forEach((receipt) => {
            newReceiptEntities.push({
              aid: apt.aid,
              doctor_oid: apt.doctor_oid,
              patient: apt.patient,
              paymentItems: receipt.receipt_payment,
              receiptSkus: receipt.receipt_sku,
              clinic_id: apt.clinic_id,
              uhid,
              receipt_number: receipt.receipt_number,
              business_receipt_number: receipt.business_receipt_number,
              receipt_id: receipt.receipt_id,
              receipt_url: receipt.receipt_url,
              remarks: receipt.remarks,
              amount_due: receipt.amount_due || 0,
              transaction_date: receipt.updated_at
                ? new Date(receipt.updated_at)
                : null,
              visited_on:
                apt.visit_start ||
                apt.visit_end ||
                apt.start_time ||
                apt.end_time
                  ? new Date(
                      apt.visit_start ||
                        apt.visit_end ||
                        apt.start_time ||
                        apt.end_time
                    )
                  : null,
              tags: apt.tags || [],
              receipt_amount: receipt.receipt_amount.toString(),
              net_amount: receipt.net_amount.toString(),
              payment_status: receipt.payment_status,
              total_discount_amount:
                receipt.additional_discount_amount +
                receipt.receipt_sku?.reduce((prev, item) => {
                  if (item.discount_amount) {
                    return prev + item.discount_amount;
                  }

                  return prev;
                }, 0),
              additional_discount_amount: receipt.additional_discount_amount,
              receipt_type: receipt.receipt_type,
            });
          });
        } else {
          newReceiptEntities.push({
            aid: apt.aid,
            patient: apt.patient,
            doctor_oid: apt.doctor_oid,
            paymentItems: [],
            receiptSkus: [],
            clinic_id: apt.clinic_id,
            uhid,
            receipt_number: undefined,
            business_receipt_number: undefined,
            receipt_id: null,
            receipt_url: null,
            remarks: "",
            transaction_date: null,
            amount_due: 0,
            visited_on:
              apt.visit_start || apt.visit_end || apt.start_time || apt.end_time
                ? new Date(
                    apt.visit_start ||
                      apt.visit_end ||
                      apt.start_time ||
                      apt.end_time
                  )
                : null,
            tags: apt.tags || [],
            receipt_amount: "",
            net_amount: "",
            payment_status: PAYMENT_STATUS.UNBILLED,
            total_discount_amount: 0,
            additional_discount_amount: 0,
            receipt_type: apt.receipts?.[0]?.receipt_type || "INCLINIC",
          });
        }

        return [...currValue, ...newReceiptEntities];
      },
      [] as Array<Omit<TReceiptEntity, "idx">>
    )
    .map((entity, index) => ({ ...entity, idx: index }));
}

export function formatReceiptDataForExcel({
  receiptItem,
  index,
  doctorNameMap,
  queueName = "",
}: {
  receiptItem: TReceiptEntity;
  index: number;
  doctorNameMap?: Record<string, string>;
  queueName?: string;
}) {
  const discount =
    receiptItem.receiptSkus?.reduce((prev, service) => {
      if (service.discount_amount) {
        return prev + service.discount_amount;
      }
      return prev;
    }, 0) ?? 0;

  const doctorName = doctorNameMap
    ? { "Doctor Name": doctorNameMap[receiptItem.doctor_oid] || "" }
    : {};

  const _queueName = queueName ? { "Queue Name": queueName } : {};

  const formattedData = {
    "Sr No": index + 1,
    ..._queueName,
    "Visit Time": `${dayjs(receiptItem.visited_on).format(
      "DD-MM-YYYY hh:mm A"
    )}`,
    ...doctorName,
    "Patient Name": receiptItem.patient.fln,
    "Appointment ID": receiptItem.aid,
    Mobile: receiptItem.patient.mobile?.replace("+91", ""),
    UHID: receiptItem?.uhid || "",
    "Payment Status": getPaymentStatusMapping(receiptItem.payment_status),
    Gender: receiptItem.patient.gen ? receiptItem.patient.gen : "",
    "Receipt Number": !!receiptItem.receipt_number
      ? receiptItem.receipt_number
      : "",
    Services:
      receiptItem.receipt_type === "DUE_PAYMENT"
        ? "Due Payment"
        : receiptItem?.receiptSkus
            ?.map((sku) => sku.sku_item.service_name)
            .join(" | "),
    "Payment Requested Date": receiptItem.transaction_date
      ? `${dayjs(receiptItem.transaction_date).format("DD-MM-YYYY")} ${dayjs(
          receiptItem.transaction_date
        ).format("hh:mm A")}`
      : "",
    Amount: !!((receiptItem.receipt_amount ?? 0) + discount)
      ? (receiptItem.receipt_amount ?? 0)
      : 0,
    "Amount Due": receiptItem.amount_due || 0,
    Discount: receiptItem.total_discount_amount || 0,
    Paid: receiptItem.net_amount ? receiptItem.net_amount : 0,
    Cash: 0,
    "Online & Other Sources": 0,
    Remarks: receiptItem.remarks || "",
  };

  receiptItem.paymentItems?.map((s) => {
    if (s.paymode) {
      if (s.paymode === RECEIPT_PAYMENT_MODE_ID.CASH) {
        formattedData["Cash"] = formattedData["Cash"] + s.amount;
      } else {
        formattedData["Online & Other Sources"] =
          formattedData["Online & Other Sources"] + s.amount;
      }
    }
  });

  return formattedData;
}

export function getServiceDropdownId({ service_id, price }: TSelectedSkuItem) {
  return `${service_id}-${price}`;
}

export function getServiceIdAndPriceFromServiceDropdownId(id: string) {
  const [service_id, price] = id.split("-");

  return { service_id: Number(service_id) || null, price: parseFloat(price) };
}

export const getIndianCurrency = (value: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(value);
};
export function getAgeInMonthsFromDob(dob: string): number | undefined {
  try {
    const currentTime = new Date().getTime();
    const dobTime = new Date(dob).getTime();
    const ageInMs = currentTime - dobTime;
    const ageTime = new Date(ageInMs);
    const ageInYears = ageTime.getFullYear() - 1970;
    let ageInMonths = ageTime.getMonth() + 1;
    ageInMonths += ageInYears * 12;
    return ageInMonths;
  } catch (e) {
    console.log("ERROR =====> getAgeInMonthsFromDob");
  }
}

export function restrictToTwoDecimals(value: string): string {
  const parts = value.split(".");
  if (parts.length > 2) {
    return parts[0] + "." + parts[1];
  }
  if (parts.length === 2 && parts[1].length > 2) {
    return parts[0] + "." + parts[1].substring(0, 2);
  }
  return value;
}
